import axios, { AxiosInstance } from "axios";
import useStore from "../store/useStore";
import { authStorage } from "./auth-storage";
import { toast } from "react-toastify";

const BASE_URL_PLAY = import.meta.env.VITE_API_URL || '/api';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

export const createApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: BASE_URL_PLAY,
    withCredentials: true,
    headers: { "Content-Type": "application/json" }
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = useStore.getState().authUser?.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const credentials = authStorage.getCredentials();
        
        if (!credentials) {
          isRefreshing = false;
          processQueue(error, null);
          
          // Limpa autenticação e redireciona para login
          useStore.getState().logout();
          localStorage.removeItem('auth');
          authStorage.clearCredentials();
          window.location.href = '/login';
          
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(`${BASE_URL_PLAY}/login`, {
            email: credentials.email,
            senha: credentials.password,
            perfil: "U",
          }, {
            headers: { "Content-Type": "application/json" }
          });

          const user = response.data;
          
          if (user && user.token) {
            // Atualiza o token no localStorage e no store
            localStorage.setItem('auth', JSON.stringify(user));
            useStore.getState().setAuthUser(user);
            
            // Processa a fila de requisições pendentes
            processQueue(null, user.token);
            
            // Refaz a requisição original com o novo token
            originalRequest.headers.Authorization = `Bearer ${user.token}`;
            return instance(originalRequest);
          } else {
            throw new Error('Token não recebido');
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Limpa autenticação e redireciona para login
          useStore.getState().logout();
          localStorage.removeItem('auth');
          authStorage.clearCredentials();
          
          toast.error("Sessão expirada. Por favor, faça login novamente.", {
            position: "top-right",
            autoClose: 3000,
          });
          
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};